import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FileStack, Loader2, Sparkles } from "lucide-react";
import { listKbGenerationTemplates } from "../services/gameRuleKbGenerationTemplateService";
import { listOpenGenerationSessions } from "../services/gameRuleKbGenerateService";
import {
  clearHostDocument,
  HOST_DOCUMENT_VIEW_ONLY_MESSAGE,
} from "../services/hostDocumentService";

export const GameRuleGenerateLandingPage = () => {
  const navigate = useNavigate();
  const [openSessionCount, setOpenSessionCount] = useState<number | null>(null);
  const [templateCount, setTemplateCount] = useState<number | null>(null);
  const [templatesLoading, setTemplatesLoading] = useState(true);
  const [clearingWord, setClearingWord] = useState(true);
  const [wordClearWarning, setWordClearWarning] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void clearHostDocument()
      .catch((err: unknown) => {
        if (cancelled) {
          return;
        }
        const message =
          err instanceof Error && err.message === HOST_DOCUMENT_VIEW_ONLY_MESSAGE
            ? HOST_DOCUMENT_VIEW_ONLY_MESSAGE
            : err instanceof Error
              ? err.message
              : "Could not clear the Word document.";
        setWordClearWarning(message);
      })
      .finally(() => {
        if (!cancelled) {
          setClearingWord(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    void listOpenGenerationSessions(100)
      .then((items) => {
        if (!cancelled) {
          setOpenSessionCount(items.length);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setOpenSessionCount(null);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setTemplatesLoading(true);
    void listKbGenerationTemplates(undefined, { limit: 1, offset: 0 })
      .then((result) => {
        if (!cancelled) {
          setTemplateCount(result.total);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setTemplateCount(null);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setTemplatesLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const startNew = () => {
    navigate("/prizelogic/generate-document/wizard", { state: { startNew: true } });
  };

  const browseTemplates = () => {
    navigate("/prizelogic/generate-document/templates");
  };

  const templateCountLabel = (() => {
    if (templatesLoading) {
      return "Loading saved templates…";
    }
    if (templateCount === null) {
      return "Browse your saved section packages";
    }
    if (templateCount === 0) {
      return "No saved templates yet";
    }
    return `${templateCount} saved template${templateCount === 1 ? "" : "s"}`;
  })();

  return (
    <div className="relative space-y-6">
      {clearingWord ? (
        <div
          className="fixed inset-0 z-[300] flex items-center justify-center bg-background/85 backdrop-blur-[2px]"
          role="status"
          aria-live="polite"
          aria-busy="true"
        >
          <div className="flex flex-col items-center gap-3 rounded-lg border border-[var(--border)] bg-card px-8 py-6 shadow-lg">
            <Loader2 className="h-9 w-9 animate-spin text-primary" aria-hidden />
            <p className="text-sm font-medium text-foreground">Preparing Word…</p>
            <p className="max-w-xs text-center text-xs text-muted-foreground">
              Clearing the document for a new game rule.
            </p>
          </div>
        </div>
      ) : null}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-foreground">Generate Document</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Start a new game rule from the Knowledgebase. Use Generated Documents to resume
            in-progress drafts.
          </p>
        </div>
        <Link to="/prizelogic" className="text-sm font-medium text-primary hover:underline shrink-0">
          Back
        </Link>
      </div>

      {wordClearWarning ? (
        <div className="rounded-lg border border-amber-500/40 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:bg-amber-950/30 dark:text-amber-200">
          {wordClearWarning}
        </div>
      ) : null}

      <div className="space-y-3">
        <button
          type="button"
          disabled={clearingWord}
          className="w-full rounded-lg bg-primary px-4 py-3 text-sm font-semibold text-primary-foreground hover:bg-primary/90 flex items-center justify-center gap-2 disabled:opacity-50"
          onClick={startNew}
        >
          <Sparkles className="h-4 w-4" aria-hidden />
          Start new game rule
        </button>

        <button
          type="button"
          disabled={clearingWord}
          className="flex w-full items-center gap-3 rounded-lg border border-[var(--border)] bg-card px-4 py-3 text-left hover:border-primary/40 disabled:opacity-50"
          onClick={browseTemplates}
        >
          <FileStack className="h-5 w-5 shrink-0 text-primary" aria-hidden />
          <span className="min-w-0 flex-1">
            <span className="block text-sm font-semibold text-foreground">
              Browse templates
            </span>
            <span className="block text-xs text-muted-foreground">
              {templateCountLabel}
            </span>
          </span>
        </button>
      </div>

      {openSessionCount !== null && openSessionCount > 0 ? (
        <p className="text-sm text-muted-foreground">
          You have {openSessionCount} in-progress draft
          {openSessionCount === 1 ? "" : "s"}.{" "}
          <Link to="/prizelogic/listing" className="font-medium text-primary hover:underline">
            View in Generated Documents
          </Link>
        </p>
      ) : null}

      <p className="text-xs text-muted-foreground leading-relaxed">
        The 5-step flow covers section selection, input fields, AI content, arrangement, and
        generation. Progress is saved when you continue between steps.
      </p>
    </div>
  );
};
